import { useCallback, useEffect, useState, useRef } from "react";
import { fetchWrapper } from "src/helpers";
import { useSocket } from "src/components/SocketContext";
import { alertService } from "src/services";
import { getLocalStorageUser } from "src/helpers/api/sharedFunction";
import WatchlistTable, {
  WatchlistData,
} from "src/components/watchlist/WatchlistTable";
import WatchlistStatsCards, {
  IWatchlistStatus,
} from "src/components/watchlist/WatchlistStatsCards";

type LtpMessage = {
  symbol_name: string;
  ltp: string;
  close: string;
  symbol_token: string;
  ltp_date: string;
};

type TrendMessage = {
  symbol: string;
  trend: "positive" | "negative";
};

type WatchlistAlertMessage = {
  id: string;
  status: string;
};

const Watchlist = () => {
  const { emit, on, off, isConnected } = useSocket();
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedRange, setSelectedRange] = useState("all");
  const [totalItems, setTotalItems] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [stockTrend, setStockTrend] = useState<
    {
      symbol: string;
      trend: "positive" | "negative";
    }[]
  >([]);
  const user = getLocalStorageUser();
  const token = user.token;

  const [sortConfig, setSortConfig] = useState<{
    key: string;
    direction: "asc" | "desc" | null;
  }>({
    key: "",
    direction: null,
  });

  const handleSort = (columnKey: string) => {
    setSortConfig((prev) => {
      if (prev.key !== columnKey) {
        return { key: columnKey, direction: "desc" as const };
      }
      if (prev.direction === "desc")
        return { key: columnKey, direction: "asc" as const };
      if (prev.direction === "asc") return { key: "", direction: null };
      return { key: columnKey, direction: "desc" as const };
    });

    setCurrentPage(1);
  };

  const [records, setRecords] = useState<WatchlistData[]>([]);
  const [watchlistStatus, setWatchlistStatus] = useState<IWatchlistStatus>({
    totalStockCount: 0,
    thisWeekStockCount: 0,
    alertTriggered: 0,
    positiveTrendCount: 0,
  });
  const [allWatchlistSymbols, setAllWatchlistSymbols] = useState<string[]>([]);
  const [filteredWatchlistIds, setFilteredWatchlistIds] = useState<Set<string>>(
    new Set(),
  );
  const [watchlistAlertItems, setWatchlistAlertItems] = useState<
    WatchlistAlertMessage[]
  >([]);

  // Custom hook to get window size
  const useWindowSize = () => {
    const [windowSize, setWindowSize] = useState({
      width: typeof window !== "undefined" ? window.innerWidth : 0,
      height: typeof window !== "undefined" ? window.innerHeight : 0,
    });

    useEffect(() => {
      const handleResize = () => {
        setWindowSize({
          width: window.innerWidth,
          height: window.innerHeight,
        });
      };

      window.addEventListener("resize", handleResize);
      handleResize();

      return () => window.removeEventListener("resize", handleResize);
    }, []);

    return windowSize;
  };

  const { width: windowWidth } = useWindowSize();
  const currentRequestRef = useRef<number>(0);
  // Calculate rows per page based on screen width
  const getRowsPerPage = () => {
    if (windowWidth >= 1999) return 20;
    if (windowWidth >= 1280) return 10;
    if (windowWidth >= 1024) return 10;
    if (windowWidth >= 768) return 10;
    return 10;
  };

  const [rowsPerPage, setRowsPerPage] = useState(getRowsPerPage());
  const params = new URLSearchParams({
    page: String(currentPage),
    pageSize: String(rowsPerPage),
    symbol: searchQuery.toUpperCase(),
    filter: selectedRange,
  });
  const getData = async () => {
    const requestId = Date.now() + Math.random();
    currentRequestRef.current = requestId;

    setIsLoading(true);
    try {
      if (sortConfig?.key && sortConfig?.direction) {
        params.append("sortColumn", sortConfig.key);
        params.append("sortOrder", sortConfig.direction);
      }
      const response = await fetchWrapper.get(
        `/api/users/watchlists?${params.toString()}`,
        undefined,
        {
          Authorization: `Bearer ${token}`,
        },
      );

      if (currentRequestRef.current !== requestId) {
        return;
      }

      setRecords(response.data.watchlist);
      setCurrentPage(response.data.pagination.page);
      setTotalItems(response.data.pagination.totalCount || 10);
      if (!searchQuery) {
        setWatchlistStatus(response.data.stats);
        setAllWatchlistSymbols(response.data.allWatchlistSymbols || []);
        setFilteredWatchlistIds(
          new Set(
            (response.data.filteredWatchlistIds || []).map(
              (id: string | number) => String(id),
            ),
          ),
        );
      }
    } catch (_error) {
      alertService.error("Something went wrong. Please try again later.");
    } finally {
      setIsLoading(false);
    }
  };
  useEffect(() => {
    if (!records.length || !isConnected) return;

    const symbols = records.map((e) => e.symbol);

    on("stock_update", handleStockUpdate);
    emit("subscribe_stock", { symbol: symbols });

    on("trend_update", handleTrendUpdate);
    emit("subscribe_trend", { symbol: allWatchlistSymbols });

    on("watchlist_update", handleWatchlistUpdate);
    emit("subscribe_watchlist", { access_token: token });

    return () => {
      off("stock_update", handleStockUpdate);
      emit("unsubscribe_stock", { symbol: symbols });
      off("trend_update", handleTrendUpdate);
      emit("unsubscribe_trend", { symbol: allWatchlistSymbols });
      off("watchlist_update", handleWatchlistUpdate);
      emit("unsubscribe_watchlist", { access_token: token });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allWatchlistSymbols.length, isLoading, isConnected]);

  const handleStockUpdate = useCallback((message: LtpMessage[]) => {
    setRecords((prev) =>
      prev.map((stock) => {
        const updated = message.find((m) => m.symbol_name === stock.symbol);
        if (!updated) return stock;

        const newLtp = parseFloat(updated.ltp);
        const close = parseFloat(updated.close);

        if (newLtp !== Number(stock.ltp)) {
          return {
            ...stock,
            ltp: newLtp,
            close,
            lastUpdated: new Date(Date.now() + 3000),
          };
        }

        return { ...stock, ltp: newLtp };
      }),
    );
  }, []);

  const handleTrendUpdate = useCallback(
    (message: TrendMessage[]) => {
      if (message.length === 0 && allWatchlistSymbols.length === 0) {
        setStockTrend([]);
      } else {
        setStockTrend(message);
      }
    },
    [allWatchlistSymbols.length],
  );

  const handleWatchlistUpdate = useCallback(
    (messages: WatchlistAlertMessage[]) => {
      setWatchlistAlertItems(messages);

      setRecords((prev) => {
        const messageMap = new Map(messages.map((m) => [m.id, m]));
        return prev.map((record) => {
          const msg = messageMap.get(record.id);
          return msg ? { ...record, status: msg.status } : record;
        });
      });
    },
    [],
  );

  useEffect(() => {
    if (records.length === 0 && stockTrend.length > 0) {
      setStockTrend([]);
    }
  }, [records.length, stockTrend.length]);

  return (
    <div className="space-y-6">
      <WatchlistStatsCards
        records={records}
        watchlistStatus={watchlistStatus}
        selectedRange={selectedRange}
        getData={getData}
        totalItems={totalItems}
        isTrial={user?.is_trial}
        positiveTrendCount={
          stockTrend.length > 0
            ? allWatchlistSymbols.filter((symbol) =>
                stockTrend.some(
                  (m) => m.symbol === symbol && m.trend === "positive",
                ),
              ).length
            : watchlistStatus.positiveTrendCount
        }
        alertTriggeredCount={
          watchlistAlertItems.length > 0
            ? watchlistAlertItems.filter(
                (m) =>
                  m.status === "Inactive" &&
                  filteredWatchlistIds.has(String(m.id)),
              ).length
            : watchlistStatus.alertTriggered
        }
      />
      <WatchlistTable
        isLoading={isLoading}
        params={params}
        getData={getData}
        setSelectedRange={setSelectedRange}
        selectedRange={selectedRange}
        records={records}
        setCurrentPage={setCurrentPage}
        currentPage={currentPage}
        totalItems={totalItems}
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        rowsPerPage={rowsPerPage}
        setRowsPerPage={setRowsPerPage}
        sortConfig={sortConfig}
        handleSort={handleSort}
      />
    </div>
  );
};

export default Watchlist;
